import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, TrashIcon, EyeIcon, PencilSquareIcon, SwatchIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Card, Spinner, Input, Badge } from '@realvisor/ui';
import { useForm, useUpdateForm, useDeleteForm, useFormSubmissions, useProjects, usePipelines } from '@/hooks';
import ApiAccessSection from './ApiAccessSection';

// Speciální typy formulářů které nelze editovat v builderu
const SPECIAL_FORM_TYPES = ['valuo', 'hypoteka'];

export default function FormDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: form, isLoading } = useForm(id || '');
  const { data: projects } = useProjects();
  const { data: pipelines } = usePipelines();
  const { data: submissionsData } = useFormSubmissions(id || '');
  const updateForm = useUpdateForm();
  const deleteForm = useDeleteForm();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
    targetPipelineId: '',
    defaultStageId: '',
  });

  const handleEdit = () => {
    if (form) {
      setFormData({
        name: form.name,
        isActive: form.isActive,
        targetPipelineId: form.targetPipelineId || '',
        defaultStageId: form.defaultStageId || '',
      });
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    
    try {
      await updateForm.mutateAsync({
        id,
        data: {
          name: formData.name,
          isActive: formData.isActive,
          targetPipelineId: formData.targetPipelineId || undefined,
          defaultStageId: formData.defaultStageId || undefined,
        },
      });
      toast.success('Formulář uložen');
      setIsEditing(false);
    } catch (error) {
      toast.error('Nepodařilo se uložit formulář');
    }
  };

  const handleDelete = async () => {
    if (!id || !form) return;
    if (!confirm(`Opravdu chcete smazat formulář "${form.name}"?`)) return;
    
    try {
      await deleteForm.mutateAsync(id);
      toast.success('Formulář smazán');
      navigate('/forms');
    } catch (error) {
      toast.error('Nepodařilo se smazat formulář');
    }
  };

  const selectedPipeline = pipelines?.find(p => p.id === (isEditing ? formData.targetPipelineId : form?.targetPipelineId));
  
  // Zjistit jestli je formulář speciální (Valuo, Hypotéka)
  const formConfig = form?.config as any;
  const isSpecialForm = formConfig?.formType && SPECIAL_FORM_TYPES.includes(formConfig.formType);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!form) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <h3 className="text-sm font-medium text-gray-900">Formulář nenalezen</h3>
          <Link to="/forms" className="mt-2 text-sm text-primary-600 hover:text-primary-700">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/forms">
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{form.name}</h1>
              <Badge variant={form.isActive ? 'success' : 'default'}>
                {form.isActive ? 'Aktivní' : 'Neaktivní'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500">
              Kód: <code className="bg-gray-100 px-1 rounded">{form.code}</code>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isSpecialForm ? (
            <Link to={`/forms/${id}/theme`}>
              <Button leftIcon={<SwatchIcon className="h-4 w-4" />}>
                Vzhled
              </Button>
            </Link>
          ) : (
            <Link to={`/forms/${id}/edit`}>
              <Button leftIcon={<PencilSquareIcon className="h-4 w-4" />}>
                Editor
              </Button>
            </Link>
          )}
          <Link to={`/forms/${id}/preview`}>
            <Button variant="outline" leftIcon={<EyeIcon className="h-4 w-4" />}>
              Náhled
            </Button>
          </Link>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Zrušit
              </Button>
              <Button onClick={handleSave} disabled={updateForm.isPending}>
                {updateForm.isPending ? 'Ukládám...' : 'Uložit'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleEdit}>
                Upravit
              </Button>
              <Button variant="danger" onClick={handleDelete} leftIcon={<TrashIcon className="h-4 w-4" />}>
                Smazat
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Form Settings */}
        <div className="lg:col-span-2 space-y-6">
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Nastavení</h2>
            
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Název formuláře
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cílová pipeline
                  </label>
                  <select
                    value={formData.targetPipelineId}
                    onChange={(e) => setFormData({ ...formData, targetPipelineId: e.target.value, defaultStageId: '' })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Výchozí pipeline</option>
                    {pipelines?.map((pipeline) => (
                      <option key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedPipeline && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Výchozí fáze
                    </label>
                    <select
                      value={formData.defaultStageId}
                      onChange={(e) => setFormData({ ...formData, defaultStageId: e.target.value })}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                    >
                      <option value="">První fáze</option>
                      {selectedPipeline.stages?.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Formulář je aktivní
                  </label>
                </div>
              </div>
            ) : (
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Projekt</dt>
                  <dd className="text-sm font-medium text-gray-900">{form.project?.name || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Pipeline</dt>
                  <dd className="text-sm font-medium text-gray-900">{form.targetPipeline?.name || 'Výchozí'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Výchozí fáze</dt>
                  <dd className="text-sm font-medium text-gray-900">{form.defaultStage?.name || 'První'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500">Přiřazování</dt>
                  <dd className="text-sm font-medium text-gray-900">{form.autoAssignStrategy}</dd>
                </div>
              </dl>
            )}
          </Card>

          {/* Embed Code */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Embed kód</h2>
            <p className="text-sm text-gray-500 mb-3">
              Vložte tento kód na váš web pro zobrazení formuláře:
            </p>
            {(() => {
              const widgetUrl = import.meta.env['VITE_WIDGET_URL'] || 'https://realvisor-com-widget.vercel.app';
              const apiUrl = import.meta.env['VITE_API_URL'] || 'https://api-production-88cf.up.railway.app';
              const themeConfig = form.config as unknown as { theme?: { primaryColor?: string; borderRadius?: string; maxWidth?: string; shadow?: string } };
              const primaryColor = themeConfig?.theme?.primaryColor || '#2563eb';
              const borderRadius = themeConfig?.theme?.borderRadius || '12px';
              const maxWidth = themeConfig?.theme?.maxWidth || '480px';
              const shadow = themeConfig?.theme?.shadow || 'lg';
              // Určit správnou funkci podle typu formuláře
              // formType může být: 'valuo', 'hypoteka', 'contact', nebo undefined/null
              const formType = formConfig?.formType;
              let createFunction = 'createContactForm'; // default pro contact a neznámé typy
              if (formType === 'valuo') {
                createFunction = 'createValuoForm';
              } else if (formType === 'hypoteka') {
                createFunction = 'createHypotekaForm';
              }
              // Pro 'contact', undefined, null nebo jakýkoliv jiný typ -> createContactForm
              
              const embedCode = `<!-- Kontejner pro formulář -->
<div id="realvisor-form"></div>

<!-- Widget script -->
<script src="${widgetUrl}/widget.iife.js"></script>
<script>
  RealvisorWidget.${createFunction}(document.getElementById('realvisor-form'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: '${form.code}',
    projectId: '${form.projectId}',
    primaryColor: '${primaryColor}',
    borderRadius: '${borderRadius}',
    maxWidth: '${maxWidth}',
    shadow: '${shadow}'
  });
</script>`;

              const multipleWidgetsCode = `<!-- Více formulářů na jedné stránce -->
<div id="form-1"></div>
<div id="form-2"></div>

<script src="${widgetUrl}/widget.iife.js"></script>
<script>
  // Každý formulář má vlastní kontejner a konfiguraci
  RealvisorWidget.${createFunction}(document.getElementById('form-1'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: '${form.code}',
    projectId: '${form.projectId}',
    primaryColor: '${primaryColor}',
    borderRadius: '${borderRadius}'
  });
  
  // Druhý formulář (může být stejný nebo jiný typ)
  RealvisorWidget.${createFunction}(document.getElementById('form-2'), {
    apiUrl: '${apiUrl}/api/v1',
    formCode: 'JINY_KOD',
    primaryColor: '#10b981',
    borderRadius: '12px'
  });
</script>`;

              return (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Základní embed</h3>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <code className="text-sm text-green-400 whitespace-pre">{embedCode}</code>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        navigator.clipboard.writeText(embedCode);
                        toast.success('Kód zkopírován');
                      }}
                    >
                      Kopírovat kód
                    </Button>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Více formulářů na stránce</h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Script stačí načíst jednou, pak můžete vytvořit libovolný počet formulářů.
                    </p>
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                      <code className="text-sm text-green-400 whitespace-pre">{multipleWidgetsCode}</code>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2">Dostupné parametry</h3>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li><code className="bg-blue-100 px-1 rounded">primaryColor</code> - hlavní barva (hex)</li>
                      <li><code className="bg-blue-100 px-1 rounded">maxWidth</code> - max. šířka ('480px', '100%')</li>
                      <li><code className="bg-blue-100 px-1 rounded">shadow</code> - stín ('none', 'sm', 'md', 'lg')</li>
                      <li><code className="bg-blue-100 px-1 rounded">borderRadius</code> - zaoblení rohů ('12px')</li>
                      <li><code className="bg-blue-100 px-1 rounded">onSuccess</code> - callback po odeslání</li>
                    </ul>
                  </div>
                </div>
              );
            })()}
          </Card>

          {/* API přístup */}
          <ApiAccessSection
            projectId={form.projectId}
            formType={formConfig?.formType}
          />
        </div>

        {/* Submissions */}
        <div>
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Poslední submissions ({submissionsData?.meta?.total || 0})
            </h2>
            
            {submissionsData?.data && submissionsData.data.length > 0 ? (
              <div className="space-y-3">
                {submissionsData.data.slice(0, 5).map((submission) => (
                  <div key={submission.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        {submission.lead?.contact?.firstName} {submission.lead?.contact?.lastName}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(submission.createdAt).toLocaleDateString('cs-CZ')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {submission.lead?.contact?.email || submission.lead?.contact?.phone}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Zatím žádné submissions
              </p>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
